import EFPicture from 'elem/compCommon/EFPicture';
import css from './index.module.scss';
import { handleMultiProductImages } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';

/**
 * 将spec参数组name，与产品的spec参数value，融合到一起
 * @param productCatagory 产品品类及spec name
 * @param productDetail spu spec value
 */
export const formatProductSpecData = (productCatagory: any, productDetail: any) => {
    if (!productCatagory || !productDetail) {
        return [];
    }
    const { skuList } = productDetail;
    if (!skuList) return [];
    const emptyValueTxt = '–';
    /**
     * 1、将genericSpec， ownSpec字符串解析成对象（此时，对象中的值仍为json字符串）
     * 2、将genericSpec， ownSpec合并到skuSpec
     */
    const formatSkuList = skuList.map((sku: any) => {
        let { ownSpec } = sku;
        const { ...others } = sku;
        ownSpec = JSON.parse(ownSpec || '{}');
        const skuSpec = { ...ownSpec };
        return { ...others, skuSpec };
    });

    const { specGroupList } = productCatagory[0] || []; // 取出参数组列表

    /**
     * 1、遍历产品品类的specGroupList
     * 2、内层遍历spu经过合并的参数组，并且生成一个specList，合入每一个specGroup中
     */
    return specGroupList
        ?.map((specGroup: any, i: number) => {
            const { specParamList, ...rst } = specGroup;
            const specList: any[] = []; // 参数列表
            const skusCount: number = formatSkuList.length;

            const { specGroupId } = specGroup;
            const obj = specGroup;
            formatSkuList.forEach((formatSku: any, skuListIndex: number) => {
                // 遍历skuList，如果遍历第二遍，证明是双品数据
                // if (skuListIndex > 1) return; // 暂不支持两个以上的双品数据 => 需支持三品数据 2025/03/27 Glacier二代 35L 45L 55L三个sku
                const { skuSpec } = formatSku;
                const specGroupJson = skuSpec[specGroupId];
                if (!specGroupJson) return;

                let specGroup: any; // 解析单个Group的JSON数据
                try {
                    specGroup = JSON.parse(specGroupJson || '{}');
                } catch {
                    specGroup = specGroupJson;
                }

                let mapArr = i !== specGroupList.length - 1 ? Object.keys(specGroup) : obj?.specParamList.map((val: any) => val.specParamId);

                // 接口变更 需按specParamList的顺序来对mapArr重新排序
                const srotMapArr: string[] = [];
                obj?.specParamList.forEach((item: { specParamId: string }) => {
                    mapArr.some((item2: string) => {
                        if (item.specParamId === item2) {
                            srotMapArr.push(item2);
                            return true;
                        }
                    });
                });

                mapArr = srotMapArr;

                mapArr.forEach((specParamId: string) => {
                    const categorySpecParam = specParamList.find((categorySpecParam: any) => categorySpecParam.specParamId === specParamId);
                    if (!categorySpecParam) return;

                    // 处理boolen值，转换为 Yes or No
                    let specValue: string;
                    if (categorySpecParam.fieldType === 'boolean' || typeof specGroup[specParamId] === 'boolean') {
                        specValue = specGroup[specParamId] ? '✓' : '-';
                        if (specGroup[specParamId] === 'false') {
                            specValue = '-';
                        }
                    } else {
                        specValue = specGroup[specParamId];
                    }
                    const specItem = specList.find((spec) => spec.specParamId === specParamId);
                    if (!specItem) {
                        // 加上单位
                        if (!Number.isNaN(Number(specValue))) {
                            specValue = specValue + (categorySpecParam.unit || '');
                        }
                        let defaultSpecValueList: any = new Array(skusCount).fill(emptyValueTxt);
                        defaultSpecValueList[skuListIndex] = specValue;

                        if (defaultSpecValueList.length <= 1) {
                            defaultSpecValueList = defaultSpecValueList.join('');
                        }

                        if (defaultSpecValueList) {
                            specList.push({
                                key: categorySpecParam.name,
                                generalKey: categorySpecParam.generalKey,
                                value: defaultSpecValueList,
                                specParamId,
                                unit: categorySpecParam.unit,
                            });
                        }

                        return;
                    }
                    if (skusCount > 1) {
                        if (!Number.isNaN(Number(specValue))) {
                            specValue = specValue + (specItem.unit || '');
                        }
                        specItem.value[skuListIndex] = specValue;
                    }
                });
            });
            return { ...rst, arrayList: specList };
        })
        ?.filter((specGroup: any) => specGroup.arrayList.length); // 过滤掉arrayList没有值的数据
};

export default function ProductSpecs({ productCatagory, productDetail, theme }: any) {
    const { useRouter } = useGlobal();
    const { locale } = useRouter();
    let skuListData = [];
    if (productDetail?.skuList?.length > 0) {
        skuListData = productDetail.skuList;
    } else {
        skuListData = [
            {
                images: handleMultiProductImages(productDetail?.images, 'specs'),
                name: productDetail?.name,
            },
        ];
    }
    // isVariant 双品：true  单品 false  => 2025/03/27 Glacier二代 35L 45L 55L三个sku （三品出现）
    const isVariant = skuListData?.length > 1;
    if (!isVariant && productDetail) {
        // 解析spu图片
        try {
            const imgObj = JSON.parse(productDetail.images);
            productDetail.spaceImages = imgObj.spaceImages || imgObj.images;
        } catch {
            productDetail.spaceImages = productDetail?.images;
        }
    }
    const specsData: any[] = formatProductSpecData(productCatagory, productDetail);

    return (
        <section className={`product-specs ${css['product-specs']} ${theme}`}>
            <div className="product-spec-box">
                <ul
                    className={`spec-img-box ${
                        skuListData?.length === 2 ? 'img-double' : skuListData?.length > 2 ? 'img-multi' : skuListData?.length === 1 ? 'img-single' : ''
                    }`}
                >
                    {isVariant &&
                        skuListData?.length > 0 &&
                        skuListData.map((item: any, i: any) => (
                            <li className={`img-list`} key={item.name + i}>
                                <h2 className="list-name">{item.name}</h2>
                                <div className="list-img">{item.images && <EFPicture src={item.images} alt={item.name} />}</div>
                            </li>
                        ))}
                    {!isVariant && skuListData?.length > 0 && (
                        <li className="img-list spec-img-single">
                            {(productDetail?.spaceImages || skuListData[0].images) && (
                                <EFPicture src={productDetail?.spaceImages || skuListData[0].images} alt={skuListData[0].name} />
                            )}
                        </li>
                    )}
                </ul>

                {Array.isArray(specsData) && (
                    <ul
                        className={`specs-catagory-params ${
                            skuListData?.length === 2
                                ? 'catagory-double'
                                : skuListData?.length > 2
                                ? 'catagory-multi'
                                : skuListData?.length === 1
                                ? 'catagory-single'
                                : ''
                        }`}
                    >
                        {specsData?.map((specGroup: any, k: number) => (
                            <li className={`catagory-item`} key={k}>
                                <div className="catagory-name">{specGroup?.name}</div>
                                <ul
                                    className={`catagory-params ${
                                        skuListData?.length === 2
                                            ? 'catagory-params-double'
                                            : skuListData?.length === 1
                                            ? 'catagory-params-single'
                                            : skuListData?.length > 2
                                            ? 'catagory-params-multi'
                                            : ''
                                    }`}
                                >
                                    {specGroup?.arrayList?.map((param: any, m: number) => (
                                        <li className="param-item" key={`${k}_${m}`}>
                                            {Array?.isArray(param?.value) && param?.value?.length > 1 ? (
                                                <ul className="param-item-list">
                                                    {param?.value?.map((paramList: any, n: number) => (
                                                        <li className="param-list" key={`${k}_${m}_${n}`}>
                                                            <p className="param-key">{param?.key}</p>
                                                            <p
                                                                className={`param-value ${
                                                                    locale === 'sa' && String(paramList).endsWith(' [* *]') ? 'special' : ''
                                                                } `}
                                                            >
                                                                {locale === 'sa' && String(paramList).endsWith(' [* *]')
                                                                    ? String(paramList)?.split(' [* *]')[0]
                                                                    : paramList}
                                                            </p>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <>
                                                    <p className="param-key">{param?.key}</p>
                                                    <p
                                                        className={`param-value ${
                                                            locale === 'sa' && String(param?.value).endsWith(' [* *]') ? 'special' : ''
                                                        } `}
                                                    >
                                                        {locale === 'sa' && String(paramList).endsWith(' [* *]')
                                                            ? String(paramList)?.split(' [* *]')[0]
                                                            : param?.value}
                                                    </p>
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}
